// src/features/text/utils/textUtils.ts

import { pascalCase, camelCase, capitalCase, constantCase, dotCase, pathCase, sentenceCase, snakeCase } from "change-case";

// --- Case Conversion Functions ---

/**
 * Converts the entire string to uppercase.
 * e.g., "hello world" -> "HELLO WORLD"
 */
export const toUpperCase = (text: string): string => text.toUpperCase();

/**
 * Converts the entire string to lowercase.
 * e.g., "HELLO WORLD" -> "hello world"
 */
export const toLowerCase = (text: string): string => text.toLowerCase();

/**
 * Capitalizes the first letter of each word.
 * This uses the 'change-case' library's capitalCase which handles various delimiters.
 * e.g., "hello world" -> "Hello World"
 */
export const toCapitalizedCase = (text: string): string => capitalCase(text);


/**
 * Converts to sentence case, capitalizing the first letter of each sentence.
 * Handles multiple sentences separated by '.', '!', '?'.
 * e.g., "hello world. new sentence." -> "Hello world. New sentence."
 */
export const toSentenceCase = (text: string): string => {
    // This regex splits the text into sentences, keeping the delimiters.
    const sentences = text.match(/[^.!?]+[.!?]*\s*/g) || [];
    return sentences
        .map(sentence => {
            const trimmedSentence = sentence.trim();
            if (trimmedSentence.length > 0) {
                return trimmedSentence.charAt(0).toUpperCase() + trimmedSentence.slice(1).toLowerCase();
            }
            return '';
        })
        .join(' ')
        .trim();
};

/**
 * Inverts the case of each character in the string.
 * e.g., "Hello World" -> "hELLO wORLD"
 */
export const toInvertedCase = (text: string): string => {
    return text.split('').map(char =>
        char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
    ).join('');
};


// --- Naming Case Conversion Functions (using change-case for robustness) ---

export {
    camelCase,         // myVariableName
    pascalCase,        // MyVariableName
    snakeCase,         // my_variable_name
    constantCase as screamingSnakeCase, // MY_VARIABLE_NAME
    dotCase,           // my.variable.name
    capitalCase as titleCase, // My Variable Name
};

// **FIXED**: Implemented trainCase manually as it's not in the library.
// It capitalizes each word and joins with a hyphen.
export const trainCase = (text: string): string => capitalCase(text).replace(/ /g, '-'); // My-Variable-Name
export const kebabCase = (text: string) => pathCase(text); // my-variable-name
export const spinalCase = kebabCase; // Alias for kebab-case
export const macroCase = (text: string) => constantCase(text); // Alias for screaming-snake-case
export const flatCase = (text: string) => text.replace(/[\s_-]/g, '').toLowerCase(); // myvariablename
export const upperCase = (text: string) => text.replace(/[\s_-]/g, '').toUpperCase(); // MYVARIABLE
export const lowerCase = flatCase; // Alias for flat-case
export const studlyCaps = (text: string) => {
    let result = '';
    // Use `pathCase` to normalize separators to spaces first
    const cleanText = pathCase(text);
    for (let i = 0; i < cleanText.length; i++) {
        if (cleanText[i] === ' ') continue;
        result += Math.random() > 0.5 ? cleanText[i].toUpperCase() : cleanText[i].toLowerCase();
    }
    return result;
}


// --- Whitespace/Trim Functions ---

export interface TrimOptions {
    trimLines: 'none' | 'start' | 'end' | 'both';
    collapseWhitespace: boolean;
    target: 'all' | 'spaces' | 'tabs';
}

/**
 * Trims whitespace from a string based on a set of options.
 * @param text The input string.
 * @param options The trimming configuration.
 * @returns The processed string.
 */
export const trimWhitespace = (text: string, options: TrimOptions): string => {
    let processedText = text;
    const { trimLines, collapseWhitespace, target } = options;

    // 1. Handle line-based trimming
    if (trimLines !== 'none') {
        processedText = processedText.split('\n').map(line => {
            if (trimLines === 'both') return line.trim();
            if (trimLines === 'start') return line.trimStart();
            if (trimLines === 'end') return line.trimEnd();
            return line;
        }).join('\n');
    }

    // 2. Handle collapsing of multiple whitespace characters
    if (collapseWhitespace) {
        let pattern;
        switch(target) {
            case 'spaces':
                pattern = / {2,}/g;
                break;
            case 'tabs':
                pattern = /\t{2,}/g;
                break;
            case 'all':
            default:
                // This will replace any sequence of 2+ spaces/tabs with a single space
                pattern = /[ \t]{2,}/g;
                break;
        }
        processedText = processedText.replace(pattern, target === 'tabs' ? '\t' : ' ');
    }

    // 3. If target is specific, remove the other type completely
    if (target === 'spaces') {
        processedText = processedText.replace(/\t/g, ''); // Remove all tabs
    } else if (target === 'tabs') {
        processedText = processedText.replace(/ /g, ''); // Remove all spaces
    }

    return processedText;
};


// --- Text Statistics Functions ---

const AVG_WORDS_PER_MINUTE = 225;

/**
 * Calculates detailed statistics for a given text.
 * @param text The input string.
 * @returns An object with various text metrics.
 */
export const getTextStats = (text: string) => {
    if (!text) {
        return {
            words: 0,
            characters: 0,
            charactersNoSpaces: 0,
            lines: 1, // An empty input is still one line
            sentences: 0,
            paragraphs: 0,
            readingTime: 0, // in minutes
        };
    }

    const words = text.match(/\b\w+\b/g) || [];
    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, '').length;
    // Correctly count lines, including the last one if it doesn't end with a newline
    const lines = (text.match(/\n/g) || []).length + 1;
    // A more robust way to count sentences
    const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) || [];
    // Paragraphs are separated by one or more empty lines
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim() !== '');

    const readingTime = Math.ceil(words.length / AVG_WORDS_PER_MINUTE);

    return {
        words: words.length,
        characters,
        charactersNoSpaces,
        lines,
        sentences: sentences.length > 0 ? sentences.length : (words.length > 0 ? 1 : 0),
        paragraphs: paragraphs.length > 0 ? paragraphs.length : (words.length > 0 ? 1 : 0),
        readingTime, // in minutes
    };
};

// --- Find & Replace Functions ---

export interface FindOptions {
    matchCase: boolean;
    wholeWord: boolean;
    isRegex: boolean;
}

export interface Match {
    text: string;
    startIndex: number;
    endIndex: number;
}

/**
 * Escapes special characters in a string for use in a regular expression.
 * @param str The string to escape.
 * @returns The escaped string.
 */
const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Finds all occurrences of a search term in a text based on options.
 * @param text The text to search within.
 * @param findTerm The term to find.
 * @param options The find configuration.
 * @returns An array of Match objects.
 * @throws An error if the regex is invalid.
 */
export const findMatches = (text: string, findTerm: string, options: FindOptions): Match[] => {
    if (!findTerm) {
        return [];
    }

    const flags = options.matchCase ? 'g' : 'gi';
    let pattern: string;

    if (options.isRegex) {
        pattern = findTerm;
    } else {
        pattern = escapeRegex(findTerm);
    }

    if (options.wholeWord) {
        pattern = `\\b${pattern}\\b`;
    }

    // This will throw an error if the regex is invalid, which we'll catch in the component
    const regex = new RegExp(pattern, flags);
    const matches: Match[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
        // Prevent infinite loops with zero-width matches
        if (match.index === regex.lastIndex) {
            regex.lastIndex++;
        }

        matches.push({
            text: match[0],
            startIndex: match.index,
            endIndex: match.index + match[0].length,
        });
    }

    return matches;
};